import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import CustomFieldOptionsEditor from './CustomFieldOptionsEditor';
import { CustomFieldType, LinkedCustomFieldObjectType } from './CustomFieldTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion, faGripLines, faTrash } from '@fortawesome/free-solid-svg-icons';
import TextInput from '../basefields/TextInput';
import TextareaInput from '../basefields/TextareaInput';
import Checkbox from '../basefields/Checkbox';
import Dropdown from '../basefields/Dropdown';
import CustomFieldPreview from './CustomFieldPreview';
import ConditionalFieldsEditor, { getConditionOptions, isSameConditionValue } from './ConditionalFieldsEditor';
import FeatureBadge from 'components/billing/FeatureBadge';
import { getFieldTypeOptions, optionPresets } from './customFieldOptionPresets';

export interface CustomFieldEditorProps {
    field: CustomFieldType;
    fields: CustomFieldType[];
    setFields: React.Dispatch<React.SetStateAction<CustomFieldType[]>>;
    selectedLinkedObjectType?: LinkedCustomFieldObjectType;
    showFieldPreview?: boolean;
    allowExtendedOptions?: boolean;
    extendedOptionsFeature?: string;
};

const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({
    field, fields, setFields, selectedLinkedObjectType, showFieldPreview, allowExtendedOptions, extendedOptionsFeature
}) => {
    const { t } = useTranslation();
    const identifier = field.id || field.uuid;

    // Determine wheter the current object type is a form or from template
    const isForm = selectedLinkedObjectType === 'form' || selectedLinkedObjectType === 'form_template';

    // Determine wheter the required setting is supported
    const supportsRequiredField = [
        'form',
        'form_template',
        'asset_type_inspection',
    ].includes(selectedLinkedObjectType || '');

    // Determine wheter the external hide setting is supported
    const supportsHideExternally = [
        'job_workflow',
        'job_workflow_appointment',
        'form',
        'form_template',
    ].includes(selectedLinkedObjectType || '');

    // Determines wheter the photo field is supported
    const supportsPhotoField = [
        'form',
        'form_template',
        'asset_type',
        'asset_type_inspection',
    ].includes(selectedLinkedObjectType || '');

    // Determine wheter the signature field is supported
    const supportsSignatureField = [
        'form',
        'form_template',
    ].includes(selectedLinkedObjectType || '');

    // Get the field type options
    const fieldTypeOptions = React.useMemo(() => {
        return getFieldTypeOptions({
            supportsPhotoField,
            supportsSignatureField,
        });
    }, [supportsPhotoField, supportsSignatureField]);

    // Determine wheter a conditional field is supported
    const supportsConditionalFields = [
        'checkbox',
        'dropdown',
        'boolean',
        'tabs',
        'yes_no',
        'acceptable',
        'compliant',
    ].includes(field.field_type);

    // Determine wheter conditional questions can be configured on this field
    const supportsConditionalQuestions = (
        (isForm || selectedLinkedObjectType === 'asset_type_inspection') &&
        supportsConditionalFields &&
        !field.parent_field_id
    );

    // Get the child fields from the fields
    const childFields = fields.filter(custom_field => custom_field.parent_field_id === identifier && !custom_field.deleted);

    // Get the answer options of this field to label the conditional questions
    const conditionOptions = getConditionOptions(field, t);

    // Sort the child fields on the ordering of their answer option
    const orderedChildFields = [...childFields].sort((firstField, secondField) => {
        const getConditionIndex = (childField: CustomFieldType) => {
            const conditionValue = field.field_type === 'boolean' ? childField.show_when_boolean : childField.show_when_option_id;
            return conditionOptions.findIndex(option => isSameConditionValue(option.value, conditionValue));
        };

        return getConditionIndex(firstField) - getConditionIndex(secondField);
    });

    // Get the answer option name a conditional question is linked to
    const getConditionOptionName = (childField: CustomFieldType) => {
        const conditionValue = field.field_type === 'boolean' ? childField.show_when_boolean : childField.show_when_option_id;
        const conditionOption = conditionOptions.find(option => isSameConditionValue(option.value, conditionValue));
        return conditionOption?.name ?? '';
    };

    // Update a field
    const updateField = (updates: Partial<CustomFieldType>) => {
        setFields(current => {
            return current.map(custom_field =>
                (custom_field.id || custom_field.uuid) === identifier
                    ? { ...custom_field, ...updates }
                    : custom_field
            );
        });
    };

    // Delete this field together with its conditional questions
    const handleDeleteField = () => {
        setFields(current => {
            return current.map(custom_field => {
                const customFieldIdentifier = custom_field.id || custom_field.uuid;

                if (customFieldIdentifier === identifier || custom_field.parent_field_id === identifier) {
                    return { ...custom_field, deleted: true };
                }

                return custom_field;
            });
        });
    };

    return (
        <div className='custom-field-card'>
            {/* Header */}
            <div className='custom-field-card-header'>
                <h6>
                    {field.label || t('custom_field.general.new_field_label')}
                </h6>
                <div className='right-items'>
                    <div className='delete-icon tooltip-icon'>
                        <FontAwesomeIcon icon={faTrash} onClick={() => handleDeleteField()} />
                        <span className="tooltip">{t('general.delete')}</span>
                    </div>
                    <div className={`order-icon tooltip-icon ${fields.length === 1 ? 'visibility-hidden' : ''}`}>
                        <FontAwesomeIcon icon={faGripLines} />
                        <span className="tooltip">{t('general.reorder')}</span>
                    </div>
                </div>
            </div>
            
            {/* Content */}
            <div className='custom-field-row'>

                {/* Field settings */}
                <div className='settings-column'>
                    <div className='form-group'>
                        <label>
                            {t('custom_field.general.label_label')}
                        </label>
                        <TextInput
                            name='label'
                            value={field.label}
                            placeholder='custom_field.general.label_placeholder'
                            onChange={(value) => {
                                updateField({
                                    label: value,
                                });
                            }}
                        />
                    </div>

                    <div className='form-group'>
                        <label>
                            {t('custom_field.general.field_type_label')}
                        </label>
                        <Dropdown<any>
                            name='field_type'
                            options={fieldTypeOptions}
                            selectedOption={
                                fieldTypeOptions.find(option =>
                                    option.value === field.field_type
                                ) ?? null
                            }
                            optionFormat='name'
                            selectionFormat={(option) => option.name}
                            allowNoneOption={false}
                            showSearch={false}
                            onChange={(value) => {
                                const selectedFieldType = value as CustomFieldType['field_type'];
                                const presetOptions = optionPresets[selectedFieldType as keyof typeof optionPresets];

                                updateField({
                                    field_type: selectedFieldType,
                                    options: presetOptions ? presetOptions.map((option, index) => ({
                                        ...option,
                                        uuid: option.uuid || uuidv4(),
                                        ordering: index + 1,
                                    })) : [],
                                });
                            }}
                        />
                    </div>

                    {(field.field_type === 'input' || field.field_type === 'textarea') && (
                        <div className='form-group'>
                            <label>
                                {t('custom_field.general.placeholder_label')}
                            </label>
                            <TextInput
                                name='placeholder'
                                value={field.placeholder || ''}
                                placeholder='custom_field.general.placeholder_label'
                                onChange={(value) => {
                                    updateField({
                                        placeholder: value,
                                    });
                                }}
                            />
                        </div>
                    )}

                    <div className='form-group'>
                        <label>
                            {t('custom_field.general.help_text_label')}
                        </label>
                        <TextareaInput
                            name='description'
                            value={field.description || ''}
                            placeholder='custom_field.general.help_text_placeholder'
                            rows={3}
                            onChange={(value) => {
                                updateField({
                                    description: typeof value === 'string' ? value : value.target.value,
                                });
                            }}
                        />
                    </div>  
                </div>

                {/* Field preview */}
                <div className='preview-column'>
                    {showFieldPreview && (
                        <div>
                            <label>
                                {t('custom_field.general.example_label')}
                            </label>
                            <CustomFieldPreview 
                                field={field} 
                            />
                        </div>
                    )}

                    {/* Set options */}
                    {(field.field_type === 'dropdown' || field.field_type === 'checkbox' || field.field_type === 'tabs') && (
                        <CustomFieldOptionsEditor
                            field={field}
                            updateField={updateField}
                        />
                    )}

                    <div className={`other-options-group ${!allowExtendedOptions ? 'disabled' : ''}`}>
                        <div className='other-options-title'>
                            <label>
                                {t('custom_field.general.other_options_label')}
                            </label>
                            {!allowExtendedOptions && (
                                <FeatureBadge feature={extendedOptionsFeature} />
                            )}
                        </div>
                        <div className='other-options'>
                            <div className='form-group'>
                                <div className='tooltip-row'>
                                    <label>
                                        {t('custom_field.general.shared_key_label')}
                                    </label>
                                    <TextInput
                                        name='shared_key'
                                        value={field.shared_key || ''}
                                        placeholder='custom_field.general.shared_key_placeholder'
                                        disabled={!allowExtendedOptions}
                                        onChange={(value) => {
                                            updateField({
                                                shared_key: value,
                                            });
                                        }}
                                    />
                                    <span className="tooltip-icon">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                        <span className="tooltip tooltip-right">
                                            {t('custom_field.general.shared_key_tooltip')}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            {supportsRequiredField && (
                                // Only allow required fields on forms and inspection fields
                                <div className='form-group'>
                                    <Checkbox
                                        name={`required_${identifier}`}
                                        label='custom_field.general.required_label'
                                        disabled={!allowExtendedOptions}
                                        align='left'
                                        value={field.required}
                                        onChange={() => {
                                            updateField({
                                                required: !field.required,
                                            });
                                        }}
                                    />
                                </div>
                            )}
                            {supportsHideExternally && (
                                // Only allow hide externally on certain object types
                                <div className='form-group tooltip-row'>
                                    <Checkbox
                                        name={`hide_externally_${identifier}`}
                                        label='custom_field.general.hide_externally_label'
                                        disabled={!allowExtendedOptions}
                                        align='left'
                                        value={field.hide_externally}
                                        onChange={() => {
                                            updateField({
                                                hide_externally: !field.hide_externally,
                                            });
                                        }}
                                    />
                                    <span className="tooltip-icon">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                        <span className="tooltip tooltip-right">
                                            {t('custom_field.general.hide_externally_tooltip')}
                                        </span>
                                    </span>
                                </div>
                            )}
                            {supportsConditionalQuestions && (
                                // Only allow conditional questions on forms and inspection fields
                                    <ConditionalFieldsEditor
                                        field={field}
                                        childFields={childFields}
                                        setFields={setFields}
                                        allowExtendedOptions={allowExtendedOptions}
                                    />
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>

            {/* Conditional questions nested under this field */}
            {supportsConditionalQuestions && orderedChildFields.length > 0 && (
                <div className='conditional-field-inset-list'>
                    {orderedChildFields.map(childField => (
                        <div key={childField.id || childField.uuid} className='conditional-field-inset'>
                            <div className='conditional-field-condition'>
                                {t('custom_field.general.shown_when_answer_label', { answer: getConditionOptionName(childField) })}
                            </div>
                            <CustomFieldEditor
                                field={childField}
                                fields={fields}
                                setFields={setFields}
                                selectedLinkedObjectType={selectedLinkedObjectType}
                                showFieldPreview={showFieldPreview}
                                allowExtendedOptions={allowExtendedOptions}
                                extendedOptionsFeature={extendedOptionsFeature}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CustomFieldEditor;